import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const simulate = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/webhooks/${order.provider}`, { order_id: order.id, outcome });
      if (outcome === "paid") {
        toast.success("Payment received");
        navigate(`/orders/${order.id}`);
      } else {
        toast.error("Payment failed (simulated)");
        setOrder({ ...order, status: "failed" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update payment");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-24 text-center text-emerald-100/50">Loading order…</div>;
  if (!order) return <div className="p-24 text-center text-emerald-100/50">Order not found.</div>;

  const providerLabel = order.provider === "pesapal" ? "PesaPal" : "NowPayments";

  return (
    <div className="mx-auto max-w-3xl px-5 py-16 lg:py-24">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-emerald-100/60 hover:text-lime-300">
        <ArrowLeft size={12} /> Back to dashboard
      </Link>

      <div className="mt-6 card-moss overflow-hidden p-8 lg:p-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="overline mb-2">Checkout · {providerLabel}</p>
            <h1 data-testid="checkout-title" className="font-editorial text-3xl font-medium text-white sm:text-4xl">
              Confirm payment
            </h1>
          </div>
          <span className={`chip ${
            order.status === "paid" ? "chip-emerald" : order.status === "failed" ? "chip-amber" : "chip-neutral"
          }`}>{order.status}</span>
        </div>

        <p className="mt-4 max-w-lg text-sm text-emerald-100/70">
          This is a <strong className="text-lime-300">sandbox checkout</strong> for {providerLabel}. Real
          gateway keys can be entered from Admin → API keys. Simulate the
          payment outcome below to complete or fail the order.
        </p>

        <div className="mt-8 divide-y divide-emerald-500/10 rounded-xl border border-emerald-500/15 bg-[#0d2416]/50">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm font-medium text-white">{i.title}</p>
                <p className="text-xs text-emerald-100/50">Qty · {i.quantity}</p>
              </div>
              <p className="font-editorial text-lime-300">${(i.price * i.quantity).toFixed(2)}</p>
            </div>
          ))}
          {order.discount > 0 && (
            <>
              <div className="flex items-center justify-between p-4">
                <p className="text-sm text-emerald-100/70">Subtotal</p>
                <p className="text-sm text-emerald-100/70">${order.subtotal.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between p-4" data-testid="checkout-discount">
                <p className="text-sm text-emerald-100/70">Discount {order.coupon_code ? `(${order.coupon_code})` : ""}</p>
                <p className="text-sm text-lime-300">−${order.discount.toFixed(2)}</p>
              </div>
            </>
          )}
          <div className="flex items-center justify-between p-4">
            <p className="text-sm text-emerald-100/70">Total</p>
            <p className="font-editorial text-2xl text-white">${order.total.toFixed(2)} <span className="text-xs text-emerald-100/50">{order.currency}</span></p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <button
            onClick={() => simulate("paid")}
            disabled={busy || order.status === "paid"}
            data-testid="checkout-simulate-paid"
            className="btn-edge justify-center disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Simulate success
          </button>
          <button
            onClick={() => simulate("failed")}
            disabled={busy || order.status === "paid"}
            data-testid="checkout-simulate-failed"
            className="btn-edge-ghost justify-center disabled:opacity-60"
          >
            <XCircle size={16} />
            Simulate failure
          </button>
        </div>

        <p className="mt-6 font-mono-edge text-[11px] text-emerald-100/40">
          ref: {order.external_ref}
        </p>
      </div>
    </div>
  );
}
